import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { ArticleCard } from "@/components/blog/ArticleCard";
import { BlogSearch } from "@/components/blog/BlogSearch";
import { BLOG_POSTS } from "@/lib/blog-posts";
import { BLOG_CATEGORIES, categoryCounts, categorySlug, FEATURED_POSTS } from "@/lib/blog-hub";

const BASE = "https://nsphysiotherapy.co.za";

export const Route = createFileRoute("/blog/")({
  component: BlogIndex,
  head: () => ({
    meta: [
      { title: "Physiotherapy Exercise Hub | Centurion Physio Guides" },
      {
        name: "description",
        content:
          "Physiotherapist written guides and exercise programmes for back pain, neck pain, sciatica, knees, shoulders and sports injuries in Centurion and Midrand.",
      },
      { property: "og:title", content: "Physiotherapy Exercise Hub | N S Physiotherapy Centurion" },
      {
        property: "og:description",
        content: "Exercise programmes, recovery guides and practical physiotherapy advice for Centurion and Midrand.",
      },
      { property: "og:url", content: `${BASE}/blog` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${BASE}/blog` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { name: "Home", url: `${BASE}/` },
            { name: "Blog", url: `${BASE}/blog` },
          ]),
        ),
      },
    ],
  }),
});

function BlogIndex() {
  const counts = categoryCounts();
  const categories = BLOG_CATEGORIES.filter((c) => (counts[c.slug] ?? 0) > 0);
  const latest = [...BLOG_POSTS]
    .sort((a, b) => (b.publishedAt > a.publishedAt ? 1 : -1))
    .slice(0, 6);

  return (
    <PageShell>
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Blog" }]} />

      <section className="container-page py-10 md:py-14">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">Physiotherapy hub</div>
          <h1 className="mt-2 font-display text-3xl font-bold text-primary md:text-4xl">
            Physiotherapy exercises and recovery guides
          </h1>
          <p className="mt-4 text-foreground/80">
            Practical, physiotherapist written guides on back pain, neck pain, sciatica, knees, shoulders and sports
            injuries, with step by step exercise programmes you can start at home in Centurion and Midrand.
          </p>
        </div>
        <div className="mt-6 max-w-2xl">
          <BlogSearch />
        </div>
      </section>

      <section className="container-page pb-4">
        <h2 className="font-display text-xl font-bold text-primary">Browse by area of pain</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((c) => (
            <li key={c.slug}>
              <Link
                to="/blog/category/$slug"
                params={{ slug: c.slug }}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/40"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold text-primary">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{counts[c.slug]} articles</span>
                </div>
                <p className="mt-2 text-sm text-foreground/70">{c.blurb}</p>
                <span className="mt-auto pt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                  View articles <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {FEATURED_POSTS.length > 0 && (
        <section className="container-page py-10">
          <h2 className="font-display text-xl font-bold text-primary">Start with our main guides</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURED_POSTS.map((p) => (
              <li key={p.slug}>
                <ArticleCard post={p} />
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="container-page pb-16">
        <h2 className="font-display text-xl font-bold text-primary">Latest articles</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {latest.map((p) => (
            <li key={p.slug}>
              <ArticleCard post={p} />
            </li>
          ))}
        </ul>

        <div className="mt-10 rounded-2xl border border-border bg-secondary/40 p-6">
          <h2 className="font-display text-lg font-bold text-primary">Need a personal assessment?</h2>
          <p className="mt-2 text-sm text-foreground/75">
            Online guidance is general information. For a diagnosis and a programme built for you, book in clinic in Eco
            Park, Centurion, or a home visit across Centurion and Midrand.
          </p>
          <Link
            to="/booking"
            className="btn-hero mt-4 inline-flex h-11 items-center rounded-full px-6 font-semibold"
          >
            Book an appointment
          </Link>
        </div>
      </section>

      <div className="container-page pb-12 text-xs text-muted-foreground">
        Articles are grouped under{" "}
        {categories.slice(0, 4).map((c, i) => (
          <span key={c.slug}>
            {i > 0 && ", "}
            <Link to="/blog/category/$slug" params={{ slug: categorySlug(c.name) }} className="hover:text-primary">
              {c.name}
            </Link>
          </span>
        ))}{" "}
        and more.
      </div>
    </PageShell>
  );
}
